<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import {
  useStore,
  useMapGetter,
  useStoreGetters,
} from 'dashboard/composables/store';
import EvolutionInstanceAPI from 'dashboard/api/channel/evolutionInstance';
import WuzapiInstanceAPI from 'dashboard/api/channel/wuzapiInstance';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import { useAccount } from 'dashboard/composables/useAccount';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ChannelIcon from 'next/icon/ChannelIcon.vue';

// wuzapi (unofficial gateway) is an isolated experiment offered only on this account.
const WUZAPI_ENABLED_ACCOUNT_IDS = [408];

const { t } = useI18n();
const { accountScopedRoute, accountId } = useAccount();
const store = useStore();
const getters = useStoreGetters();
const inboxes = useMapGetter('inboxes/getInboxes');

const showDeletePopup = ref(false);
const selectedInbox = ref({});
const showConnectPopup = ref(false);
const selectedConnectionInbox = ref({});
const connectionStatus = ref('');
const qrCode = ref('');
const isFetchingConnection = ref(false);
const connectionError = ref('');
const instanceStatuses = ref({});
let pollingTimer = null;
let statusPollingTimer = null;

const uiFlags = computed(() => getters['inboxes/getUIFlags'].value);

const isWuzapiAccount = computed(() =>
  WUZAPI_ENABLED_ACCOUNT_IDS.includes(accountId.value)
);

// The "New instance" CTA targets the provider relevant to this account
// (wuzapi on the isolated experiment account, evolution everywhere else).
const newInstanceProvider = computed(() =>
  isWuzapiAccount.value ? 'wuzapi' : 'evolution'
);

// Lists both unofficial providers. Accounts without wuzapi inboxes (i.e. everyone
// except the experiment account) see exactly the same evolution-only list as before.
const unofficialInboxes = computed(() => {
  return (inboxes.value || [])
    .filter(
      inbox =>
        inbox.channel_type === INBOX_TYPES.WHATSAPP &&
        ['evolution', 'wuzapi'].includes(inbox.provider)
    )
    .slice()
    .sort((a, b) => a.name.localeCompare(b.name));
});

const apiFor = inbox =>
  inbox?.provider === 'wuzapi' ? WuzapiInstanceAPI : EvolutionInstanceAPI;

const confirmDeleteMessage = computed(
  () => `${t('INBOX_MGMT.DELETE.CONFIRM.MESSAGE')} ${selectedInbox.value.name}?`
);

const deleteConfirmText = computed(
  () => `${t('INBOX_MGMT.DELETE.CONFIRM.YES')} ${selectedInbox.value.name}`
);

const deleteRejectText = computed(
  () => `${t('INBOX_MGMT.DELETE.CONFIRM.NO')} ${selectedInbox.value.name}`
);

const confirmPlaceHolderText = computed(
  () =>
    `${t('INBOX_MGMT.DELETE.CONFIRM.PLACE_HOLDER', {
      inboxName: selectedInbox.value.name,
    })}`
);

const openDelete = inbox => {
  selectedInbox.value = inbox;
  showDeletePopup.value = true;
};

const closeDelete = () => {
  selectedInbox.value = {};
  showDeletePopup.value = false;
};

const confirmDeletion = async () => {
  try {
    await store.dispatch('inboxes/delete', selectedInbox.value.id);
    useAlert(t('INBOX_MGMT.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('INBOX_MGMT.DELETE.API.ERROR_MESSAGE'));
  } finally {
    closeDelete();
  }
};

function clearPolling() {
  if (pollingTimer) {
    clearInterval(pollingTimer);
    pollingTimer = null;
  }
}

function clearStatusPolling() {
  if (statusPollingTimer) {
    clearInterval(statusPollingTimer);
    statusPollingTimer = null;
  }
}

const closeConnectPopup = () => {
  showConnectPopup.value = false;
  selectedConnectionInbox.value = {};
  connectionStatus.value = '';
  qrCode.value = '';
  connectionError.value = '';
  clearPolling();
};

async function refreshConnectionStatus() {
  if (!selectedConnectionInbox.value.id) return;

  try {
    isFetchingConnection.value = true;
    connectionError.value = '';
    const response = await apiFor(selectedConnectionInbox.value).getStatus(
      selectedConnectionInbox.value.id
    );
    connectionStatus.value = response.data.connected
      ? 'connected'
      : response.data.connection_state || 'pending';
    qrCode.value = response.data.qr_code || '';
  } catch (error) {
    connectionError.value =
      error?.response?.data?.error ||
      t('INBOX_MGMT.INSTANCES.CONNECTION_ERROR');
  } finally {
    isFetchingConnection.value = false;
  }
}

const startPolling = () => {
  clearPolling();
  pollingTimer = setInterval(() => {
    if (!showConnectPopup.value) return;
    if (connectionStatus.value === 'connected') return;

    refreshConnectionStatus();
  }, 10000);
};

const statusMeta = inboxId => {
  return (
    instanceStatuses.value[inboxId] || {
      key: 'disconnected',
      label: t('INBOX_MGMT.INSTANCES.DISCONNECTED'),
    }
  );
};

const statusBadgeClass = inboxId => {
  const status = statusMeta(inboxId).key;

  if (status === 'connected') {
    return 'bg-green-50 text-green-700 ring-green-600/20';
  }

  if (status === 'connecting') {
    return 'bg-amber-50 text-amber-700 ring-amber-600/20';
  }

  return 'bg-ruby-50 text-ruby-700 ring-ruby-600/20';
};

const normalizeConnectionState = response => {
  if (response.connected) {
    return {
      key: 'connected',
      label: t('INBOX_MGMT.INSTANCES.CONNECTED_BADGE'),
    };
  }

  const state = response.connection_state?.toString().toLowerCase() || '';
  if (
    ['connecting', 'pairing', 'qrcode', 'qr', 'open'].some(value =>
      state.includes(value)
    )
  ) {
    return {
      key: 'connecting',
      label: t('INBOX_MGMT.INSTANCES.CONNECTING'),
    };
  }

  return {
    key: 'disconnected',
    label: t('INBOX_MGMT.INSTANCES.DISCONNECTED'),
  };
};

async function refreshInstanceStatuses() {
  if (!unofficialInboxes.value.length) {
    instanceStatuses.value = {};
    return;
  }

  const results = await Promise.allSettled(
    unofficialInboxes.value.map(async inbox => {
      const response = await apiFor(inbox).getStatus(inbox.id);
      return [inbox.id, normalizeConnectionState(response.data)];
    })
  );

  instanceStatuses.value = results.reduce((acc, result, index) => {
    const inboxId = unofficialInboxes.value[index]?.id;
    if (!inboxId) return acc;

    if (result.status === 'fulfilled') {
      const [id, status] = result.value;
      acc[id] = status;
      return acc;
    }

    acc[inboxId] = {
      key: 'disconnected',
      label: t('INBOX_MGMT.INSTANCES.DISCONNECTED'),
    };
    return acc;
  }, {});
}

const startStatusPolling = () => {
  clearStatusPolling();
  statusPollingTimer = setInterval(refreshInstanceStatuses, 15000);
};

const openConnectPopup = async inbox => {
  selectedConnectionInbox.value = inbox;
  showConnectPopup.value = true;
  await refreshConnectionStatus();
  startPolling();
};

onMounted(() => {
  store.dispatch('inboxes/get');
  startStatusPolling();
});

onBeforeUnmount(() => {
  clearPolling();
  clearStatusPolling();
});

watch(
  unofficialInboxes,
  () => {
    refreshInstanceStatuses();
  },
  { immediate: true }
);
</script>

<template>
  <SettingsLayout
    :no-records-found="!unofficialInboxes.length"
    :no-records-message="$t('INBOX_MGMT.INSTANCES.EMPTY')"
    :is-loading="uiFlags.isFetching"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('INBOX_MGMT.INSTANCES.TITLE')"
        :description="$t('INBOX_MGMT.INSTANCES.DESCRIPTION')"
        feature-name="inboxes"
      >
        <template #actions>
          <router-link
            :to="{
              name: 'settings_inboxes_page_channel',
              params: { sub_page: 'whatsapp' },
              query: { provider: newInstanceProvider },
            }"
          >
            <Button
              icon="i-lucide-circle-plus"
              :label="$t('INBOX_MGMT.INSTANCES.NEW_INSTANCE')"
            />
          </router-link>
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <table class="min-w-full overflow-x-auto">
        <tbody class="flex-1 divide-y text-n-slate-12 divide-n-weak">
          <tr v-for="inbox in unofficialInboxes" :key="inbox.id">
            <td class="py-4 ltr:pr-4 rtl:pl-4">
              <div class="flex flex-row gap-4 items-center">
                <div
                  class="flex justify-center items-center p-2 rounded-full border shadow-sm size-12 bg-n-alpha-3 ring ring-n-solid-1 border-n-strong"
                >
                  <ChannelIcon class="size-5 text-n-slate-10" :inbox="inbox" />
                </div>
                <div>
                  <div class="flex gap-2 items-center">
                    <span class="block font-medium capitalize">
                      {{ inbox.name }}
                    </span>
                    <span
                      class="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full ring-1 ring-inset"
                      :class="statusBadgeClass(inbox.id)"
                    >
                      {{ statusMeta(inbox.id).label }}
                    </span>
                  </div>
                  <span class="block text-sm text-n-slate-11">
                    {{ inbox.phone_number }}
                  </span>
                  <span class="block text-xs text-n-slate-10">
                    {{ inbox.provider_config?.instance_name || '-' }}
                  </span>
                </div>
              </div>
            </td>
            <td class="py-4">
              <div class="flex gap-1 justify-end">
                <router-link
                  :to="{
                    name: 'settings_inbox_show',
                    params: { inboxId: inbox.id },
                  }"
                >
                  <Button
                    icon="i-lucide-settings"
                    slate
                    xs
                    faded
                    :label="$t('INBOX_MGMT.SETTINGS')"
                  />
                </router-link>
                <Button
                  icon="i-lucide-qr-code"
                  xs
                  slate
                  faded
                  :label="$t('INBOX_MGMT.INSTANCES.CONNECT_QR')"
                  @click="openConnectPopup(inbox)"
                />
                <Button
                  icon="i-lucide-trash-2"
                  xs
                  ruby
                  faded
                  :label="$t('INBOX_MGMT.DELETE.BUTTON_TEXT')"
                  @click="openDelete(inbox)"
                />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </template>

    <woot-confirm-delete-modal
      v-if="showDeletePopup"
      v-model:show="showDeletePopup"
      :title="$t('INBOX_MGMT.DELETE.CONFIRM.TITLE')"
      :message="confirmDeleteMessage"
      :confirm-text="deleteConfirmText"
      :reject-text="deleteRejectText"
      :confirm-value="selectedInbox.name"
      :confirm-place-holder-text="confirmPlaceHolderText"
      @on-confirm="confirmDeletion"
      @on-close="closeDelete"
    />

    <woot-modal v-model:show="showConnectPopup" :on-close="closeConnectPopup">
      <div class="p-6">
        <h3 class="mb-2 text-lg font-medium text-n-slate-12">
          {{ $t('INBOX_MGMT.INSTANCES.CONNECT_TITLE') }}
        </h3>
        <p class="mb-4 text-sm text-n-slate-11">
          {{ selectedConnectionInbox.name }}
        </p>

        <div class="flex flex-col gap-3 items-center">
          <p class="text-sm text-n-slate-11">
            {{
              `${$t('INBOX_MGMT.INSTANCES.STATUS')}: ${connectionStatus || '-'}`
            }}
          </p>
          <img
            v-if="qrCode && connectionStatus !== 'connected'"
            :src="qrCode"
            alt="WhatsApp QR Code"
            class="w-64 h-64 rounded border border-n-weak"
          />
          <p
            v-else-if="connectionStatus === 'connected'"
            class="text-sm text-green-600"
          >
            {{ $t('INBOX_MGMT.INSTANCES.CONNECTED') }}
          </p>
          <p v-else-if="!isFetchingConnection" class="text-sm text-n-slate-11">
            {{ $t('INBOX_MGMT.INSTANCES.WAITING_QR') }}
          </p>
          <p v-if="connectionError" class="text-sm text-ruby-600">
            {{ connectionError }}
          </p>
        </div>

        <div class="flex gap-2 justify-end mt-6">
          <Button
            :label="$t('INBOX_MGMT.INSTANCES.REFRESH')"
            slate
            :is-loading="isFetchingConnection"
            @click="refreshConnectionStatus"
          />
          <router-link
            :to="
              accountScopedRoute('settings_inbox_show', {
                inboxId: selectedConnectionInbox.id,
              })
            "
          >
            <Button :label="$t('INBOX_MGMT.SETTINGS')" />
          </router-link>
        </div>
      </div>
    </woot-modal>
  </SettingsLayout>
</template>
